import useSWR from "swr";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  get,
  map,
  first,
  slice,
  filter,
  isEmpty,
  chain,
  forEach,
  find,
  toNumber,
} from "lodash";
import { productPage } from "grandus-lib/utils/fetches";
import Link from "next/link";
import EnhancedEcommerce from "grandus-lib/utils/ecommerce";
import TagManager from "grandus-lib/utils/gtag";
import { reqGetHeadersFront } from "grandus-lib/utils";

import styles from "./product.page.module.scss";
import Image from "grandus-lib/components-atomic/image/Image";

import dynamic from "next/dynamic";
import useWishlist from "grandus-lib/hooks/useWishlist";
import useCompare from "grandus-lib/hooks/useCompare";
import MicroData, {
  MICRODATA_PRODUCT,
} from "grandus-lib/components-atomic/MicroData";
import { UTC_OFFSET_MINUTES } from "constants/AppConstants";

const MetaData = dynamic(() =>
  import("grandus-lib/components-atomic/MetaData")
);
const Breadcrumbs = dynamic(() => import("components/breadcrumbs/Breadcrumbs"));
const AddToCartButton = dynamic(
  () => import("components/cart/AddToCartButton"),
  { ssr: false }
);
import Price from "grandus-lib/components-atomic/price/Price";
const CrossSale = dynamic(() => import("components/product/detail/crossSale"), {
  ssr: false,
});

import Gallery from "components/gallery/Gallery";
import EnergyLabel from "components/product/detail/energyLabel/EnergyLabel";
import PackageProducts from "components/product/detail/packageProducts/PackageProducts";
import PackageGallery from "components/product/detail/packageProducts/PackageGallery";
import Parameters from "components/product/detail/parameters/Parameters";
import { Carousel } from "antd";
import useMember from "utils/useMember";
import ProductLabelsPhoto from "components/product/card/ProductLabelsPhoto";
import ProductLabelsBase from "components/product/card/ProductLabelsBase";
import CustomGaEvents from "utils/customGA";
import {MinPrice} from "components/product/detail/minPrice/MinPrice";

dayjs.extend(utc); // use UTC + offset 120min (Europe/Bratislava) because of different timezones

const CompareButton = ({ productId }) => {
  const { isLoading, itemAdd, itemRemove, itemExists } = useCompare();

  const exists = itemExists(productId);
  const text = isLoading
    ? "načítavam"
    : !exists
    ? "pridať do porovnania"
    : "odstrániť z porovnania";

  const icon = isLoading
    ? "fas fa-spinner fa-spin"
    : !exists
    ? "fas fa-balance-scale"
    : "fas fa-balance-scale-right";

  return (
    <span
      className="compare"
      onClick={() =>
        !isLoading
          ? exists
            ? itemRemove(productId)
            : itemAdd(productId)
          : null
      }
    >
      <i className={icon} aria-hidden="true"></i>
      {text}
    </span>
  );
};

const WishlistButton = ({ productId }) => {
  const { isLoading, itemAdd, itemRemove, itemExists } = useWishlist();

  const exists = itemExists(productId);
  const text = isLoading
    ? "načítavam"
    : !exists
    ? "pridať do obľúbených"
    : "odstrániť z obľúbených";

  const icon = isLoading
    ? "fas fa-spinner fa-spin"
    : !exists
    ? "far fa-heart"
    : "fas fa-heart";

  return (
    <span
      className="compare"
      onClick={() =>
        !isLoading
          ? itemExists(productId)
            ? itemRemove(productId)
            : itemAdd(productId)
          : null
      }
    >
      <i className={icon} aria-hidden="true"></i>
      {text}
    </span>
  );
};

const EshopAvailability = ({ product }) => {
  const { member, operationUnits } = useMember();
  const districtId = get(
    operationUnits,
    [member?.district, 0, "town", "district", "id"],
    null
  );
  const { data: availableStores, isValidating: isLoading } = useSWR(
    districtId && product
      ? `/api/pages/product/${product.id}/availability?districtId=${districtId}`
      : null
  );

  if (isLoading) {
    return <i className="fas fa-spinner fa-spin"></i>;
  }

  if (!isEmpty(availableStores)) {
    const globalStoresAvailability = !isEmpty(
      find(availableStores, (store) => toNumber(store?.globalDelivery))
    );
    const localStoresAvailability = !isEmpty(
      find(
        availableStores,
        (store) =>
          toNumber(store?.localDelivery) &&
          store?.town?.district?.name === member?.district
      )
    );

    const available =
      product?.storeStatus?.available &&
      availableStores.length > 0 &&
      (globalStoresAvailability || localStoresAvailability);

    let availabilityTimeMessage = "";
    if (available) {
      const now = dayjs().utc().utcOffset(UTC_OFFSET_MINUTES).format("HH:mm");
      const days = now < "12:00" ? "1 deň" : "2 dni"; // 1 day before 12:00, 2 days otherwise
      availabilityTimeMessage = ` - o ${days} u Vás doma`;
    }
    return (
      <>
        <div className="item">
          <span className={`icon ${available ? "green" : "red"}`}>
            <i className="fas fa-warehouse"></i>
          </span>
          <p>
            <strong>{available ? "na sklade" : "na objednávku"}</strong>
            {availabilityTimeMessage}
            {/* <strong>{product?.storeStatus?.message}</strong>
                  {product?.storeStatus.available
                    ? " - o 2 dni u Vás doma"
                    : ""} */}
          </p>
        </div>
        {/* <div className="item">
              <span className="icon"><i className="fas fa-truck"></i></span>
              <p><strong>Doprava zadarmo</strong></p>
          </div> */}
      </>
    );
  }

  return null;
};

const ShowMoreDiscountsButton = ({
  marketingSetsCount,
  onClick,
  showMoreMarketingSets,
}) => {
  if (marketingSetsCount < 1) {
    return null;
  }
  let textPrefix = `Zobraziť ďalšiu`;
  let textSuffix = `akciu`;
  if (marketingSetsCount >= 2) {
    textPrefix = `Zobraziť ďalšie`;
    textSuffix = `akcie`;
  }
  if (marketingSetsCount >= 4) {
    textPrefix = `Zobraziť ďalších`;
    textSuffix = `akcií`;
  }

  return (
    <button className="show-more js-show-more" onClick={onClick}>
      {textPrefix} <span>{marketingSetsCount}</span> {textSuffix}{" "}
      <i
        className={`fas ${
          showMoreMarketingSets ? `fa-caret-up` : `fa-caret-down`
        }`}
      ></i>
    </button>
  );
};

const ArrowLeft = (props) => {
  const { className, style, onClick } = props;
  return (
    <button type="button" className={className + " left"} onClick={onClick}>
      {/* <i className="fas fa-chevron-left"></i> */}
    </button>
  );
};

const ArrowRight = (props) => {
  const { className, style, onClick } = props;
  return (
    <button type="button" className={className + " right"} onClick={onClick}>
      {/* <i className="fas fa-chevron-right"></i> */}
    </button>
  );
};

const Product = (props) => {
  const router = useRouter();
  const { data: product } = useSWR(
    `/api/lib/v1/products/${get(router, "query.id")}?hash=${get(
      router,
      "query.hash"
    )}`,
    (url) =>
      fetch(url, {
        headers: reqGetHeadersFront(null, { forwardUrl: router.asPath }),
      }).then((r) => r.json()),
    {
      initialData: props.product,
      revalidateOnMount: true,
    }
  );

  const [activeTab, setActiveTab] = useState(
    get(product, "productDescription.description") ? 1 : 2
  );
  const [activeSection, setActiveSection] = useState(1);
  const [showMoreMarketingSets, setShowMoreMarketingSets] = useState(false);
  const { store } = product;
  useEffect(() => {
    TagManager.push(EnhancedEcommerce.detail(props?.product));
    TagManager.push(EnhancedEcommerce.view_item(props?.product));
    TagManager.push(CustomGaEvents.pageType("Product"));
  }, []);

  const availableStores = filter(
    get(store, "[0].storeData", []),
    (storeData) => storeData?.storeCount > 0
  );

  const marketingSets = filter(get(product, "marketingSets", []), [
    "showOnProductDetail",
    true,
  ]);
  const firstMarketingSet = first(marketingSets);
  const otherMarketingSets = slice(marketingSets, 1);

  const campaigns = chain(marketingSets)
    .map((set) => get(set, "marketingCampaigns"))
    .uniqBy("id")
    .orderBy(["priority"], ["desc"])
    .head()
    .value();

  const title = {
    prefixes: [],
    suffixes: [],
  };

  forEach(marketingSets, (set) => {
    const { prefix, suffix } = set;
    if (prefix) {
      title.prefixes.push(prefix);
    }
    if (suffix) {
      title.suffixes.push(suffix);
    }
  });

  const isPackage = !isEmpty(get(product, "productPackageProducts", []));

  return (
    <>
      <section className="product-detail" style={{ paddingTop: "50px" }}>
        <MetaData
          title={get(product, "name")}
          {...get(product, "meta", {})}
          photo={get(product, "photo", {})}
        />
        <MicroData type={MICRODATA_PRODUCT} data={product} />

        <div className="products-container">
          <Breadcrumbs
            data={get(product, "breadcrumbs", [])}
            current={get(product, "name")}
          />
          <div className="row">
            <div className="col-lg-12 hidden-md-up mb2">
              <div className="main">
                <div className="manufacturer">{product?.brand?.name}</div>
                <h1>{product?.name}</h1>
                <div className="parameters">
                  <span>Kód výrobcu: {product?.partNo}</span>
                  <span>EAN:{product?.ean}</span>
                  <span>Grandus ID: {product?.id}</span>
                </div>
              </div>
            </div>
            <div className="col-xl-6">
              {isPackage ? (
                <PackageGallery product={product} />
              ) : (
                <Gallery
                  className="gallery-main-center"
                  classNameMainPhoto="gallery-relative"
                  titleImage={get(product, "photo", null)}
                  images={get(product, "gallery", [])}
                  showThumbnails
                  options={{
                    imageMainProps: {
                      size: "390",
                    },
                    imageThumbnailProps: {
                      size: "106__cropped",
                    },
                  }}
                >
                  <ProductLabelsBase product={product} />
                  <ProductLabelsPhoto product={product} />
                </Gallery>
              )}
              {!isEmpty(campaigns) ? (
                <section className="promo gallery-main-center">
                  <Carousel
                    className="promo-slider"
                    {...{
                      dots: false,
                      arrows: true,
                      infinite: true,
                      draggable: true,
                      prevArrow: <ArrowLeft />,
                      nextArrow: <ArrowRight />,
                      autoplay: true,
                      autoplaySpeed: 5000,
                    }}
                  >
                    {map(campaigns, (campaign, index) => {
                      const { id, urlName, coverPhoto } = campaign;
                      return (
                        <div
                          key={`campaign-${id}-${index}`}
                          className={styles["homepage-banner"]}
                        >
                          <Link
                            href="/akcie/[campaign]/[[...parameters]]"
                            as={`/akcie/${urlName}`}
                          >
                            <a className="product-image-antd">
                              <Image
                                photo={coverPhoto}
                                size={"590x210"}
                                className=""
                                type={"jpg"}
                              />
                            </a>
                          </Link>
                        </div>
                      );
                    })}
                  </Carousel>
                </section>
              ) : null}

              {/* <div className="present">
                  <div className="image">
                      <img src="assets/img/present.jpg" alt="present" />
                  </div>
                  <div className="present-text">
                      <h3>Darček zadarmo v hodnote 60 €</h3>
                      <p><strong>Samsung WMN5870XR/XC</strong> - Držiak na stenu pre 75"-85" Samsung TV 2017-2019</p>
                  </div>
              </div> */}
            </div>
            <div className="col-xl-5 offset-xl-1">
              <div className="main">
                {!isEmpty(marketingSets) ? (
                  <div className="discounts">
                    <div className="first-item">
                      {get(firstMarketingSet, "labels.0") ? (
                        <div className="label">
                          {get(firstMarketingSet, "labels.0.photo") ? (
                            <Image
                              photo={{
                                ...get(firstMarketingSet, "labels.0.photo"),
                                ...{
                                  path: `${get(
                                    firstMarketingSet,
                                    "labels.0.photo.path"
                                  )}/${get(
                                    firstMarketingSet,
                                    "labels.0.photo.id"
                                  )}`,
                                },
                              }}
                              size={"17x17__cropped"}
                              type={"png"}
                            />
                          ) : null}{" "}
                          {get(firstMarketingSet, "labels.0.name")}
                          {/* <i className="fas fa-star"></i>Dnešná akcia */}
                        </div>
                      ) : null}

                      <h3>{get(firstMarketingSet, "productDetailName")}</h3>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: get(
                            firstMarketingSet,
                            "productDetailDescription",
                            ""
                          ),
                        }}
                      />
                      <Link
                        href="/akcie/[campaign]/[[...parameters]]"
                        as={`/akcie/${get(
                          firstMarketingSet,
                          "marketingCampaigns[0].urlName"
                        )}`}
                      >
                        <a>Viac informácií a podmienky.</a>
                      </Link>
                      {otherMarketingSets.length > 0 ? (
                        <ShowMoreDiscountsButton
                          marketingSetsCount={otherMarketingSets.length}
                          onClick={() =>
                            setShowMoreMarketingSets(!showMoreMarketingSets)
                          }
                          showMoreMarketingSets={showMoreMarketingSets}
                        />
                      ) : null}
                    </div>
                    {map(otherMarketingSets, (marketingSet, index) => (
                      <div
                        key={`marketing-set-${marketingSet?.id}-${index}`}
                        className={`js-discount-items ${
                          !showMoreMarketingSets ? "next-items" : ""
                        }`}
                      >
                        <div className="discount-item">
                          {get(marketingSet, "labels.0.photo") ? (
                            <div className="image">
                              <Image
                                photo={{
                                  ...get(marketingSet, "labels.0.photo"),
                                  ...{
                                    path: `${get(
                                      marketingSet,
                                      "labels.0.photo.path"
                                    )}/${get(marketingSet, "labels.0.photo.id")}`,
                                  },
                                }}
                                size={"53x53"}
                                type={"png"}
                              />
                            </div>
                          ) : null}
                          <div className="text">
                            <h3>{get(marketingSet, "productDetailName")}</h3>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: get(
                                  marketingSet,
                                  "productDetailDescription",
                                  ""
                                ),
                              }}
                            />
                          </div>
                          {/* <a href="#" className="button">
                            Viac info
                          </a> */}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="hidden-md-down">
                  <div className="hidden" id="flix-minisite"></div>
                  <div className="manufacturer">{product?.brand?.name}</div>
                  <h1>
                    {!isEmpty(title.prefixes) ? title.prefixes.join(" ") : ""}
                    {product?.name}
                    {!isEmpty(title.suffixes)
                      ? ` + ${title.suffixes.join(" + ")}`
                      : ""}
                  </h1>
                  <div className="parameters">
                    <span>Kód výrobcu: {product?.partNo}</span>
                    <span>EAN:{product?.ean}</span>
                    <span>Grandus ID: {product?.id}</span>
                  </div>
                </div>
                <div className="desc" style={{ margin: "15px 0" }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: get(product, "shortProductDescription.description"),
                    }}
                  />
                  <a href="#longDesc">Celý popis</a>
                </div>
                <div className="tabs-section">
                  <div className="heading hidden-sm-down">
                    <span
                      onClick={() => setActiveSection(1)}
                      className={`tab ${activeSection === 1 ? `active` : ""}`}
                    >
                      <i className="fas fa-laptop"></i>
                      E-shop
                      {/* <span>
                        ({get(product, "store[0].count", 0)}{" "}
                        {get(product, "store[0].name", "ks")})
                      </span> */}
                    </span>
                    <span
                      onClick={() => setActiveSection(2)}
                      className={`tab ${activeSection === 2 ? `active` : ""}`}
                    >
                      <i className="fas fa-store"></i>
                      Predajne
                      {availableStores.length > 0 ? (
                        <span>
                          (dnes v{" "}
                          <strong>
                            {availableStores.length === 1
                              ? "1 predajni"
                              : `${availableStores.length} predajniach`}
                          </strong>
                          )
                        </span>
                      ) : (
                        <span>(nedostupné)</span>
                      )}
                    </span>
                  </div>
                  <div className="content">
                    <div
                      onClick={() => {
                        if (activeSection === 1) {
                          setActiveSection(2);
                        } else {
                          setActiveSection(1);
                        }
                      }}
                      className="tab-heading hidden-sm-up"
                    >
                      E-shop{" "}
                      <i
                        className={`fas ${
                          activeSection === 1 ? "fa-caret-up" : "fa-caret-down"
                        }`}
                      ></i>
                    </div>
                    <div
                      className={`tab ${activeSection === 1 ? `show` : ""}`}
                      id="eshop"
                    >
                      <div className="top">
                        <div className="price-wrap">
                          <Price
                            priceData={product?.finalPriceData}
                            options={{
                              hideVatPrice: true,
                              mainPriceClass: "price",
                            }}
                          />
                          {isPackage ? (
                            product?.standardPriceData?.price >
                            product?.finalPriceData?.price ? (
                              <span className="old-price">
                                <Price
                                  priceData={product?.standardPriceData}
                                  options={{ hideVatPrice: true }}
                                />
                              </span>
                            ) : (
                              ""
                            )
                          ) : product?.priceData?.price !==
                            product?.finalPriceData?.price ? (
                            <span className="old-price">
                              <Price
                                priceData={product?.priceData}
                                options={{ hideVatPrice: true }}
                              />
                            </span>
                          ) : (
                            ""
                          )}
                          {product?.minPriceInLastDays?.priceFormatted?
                              <MinPrice minPriceInLastDays={product?.minPriceInLastDays}/>:""
                          }
                        </div>

                        <AddToCartButton
                          stores={get(product, "store", [])}
                          product={product}
                        />
                      </div>
                      <div className="availibility-info">
                        <EshopAvailability product={product} />
                        {/* <div className="item">
                          <span className="icon">
                            <i className="fas fa-warehouse"></i>
                          </span>
                          <p>
                            <strong>{product?.storeStatus?.message}</strong>
                            {product?.storeStatus.available
                              ? " - o 2 dni u Vás doma"
                              : ""}
                          </p>
                        </div> */}
                        {/* <div className="item">
                                          <span className="icon"><i className="fas fa-truck"></i></span>
                                          <p><strong>Doprava zadarmo</strong></p>
                                      </div> */}
                      </div>
                    </div>
                    <div
                      onClick={() => {
                        if (activeSection === 2) {
                          setActiveSection(1);
                        } else {
                          setActiveSection(2);
                        }
                      }}
                      className="tab-heading hidden-sm-up"
                    >
                      Predajne{" "}
                      <i
                        className={`fas ${
                          activeSection === 2 ? "fa-caret-up" : "fa-caret-down"
                        }`}
                      ></i>
                    </div>
                    <div
                      className={`tab ${activeSection === 2 ? `show` : ""}`}
                      id="predajne"
                    >
                      <div className="stores-detail">
                        {availableStores.length ? (
                          <div className="table-wrap">
                            <table>
                              <tbody>
                                {map(availableStores, (entry, index) => {
                                  return (
                                    <tr key={`product-detail-store-${index}`}>
                                      <td>
                                        <strong>{entry?.storeName}</strong>
                                      </td>
                                      <td>
                                        {entry?.storeCount > 0 ? (
                                          <>
                                            {entry?.storeCount > 5
                                              ? "5+"
                                              : entry?.storeCount}{" "}
                                            {get(product, "store[0].name", "ks")}
                                          </>
                                        ) : (
                                          "Na objednávku"
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <span>
                            Ľutujeme, ale produkt sa fyzicky nenachádza u žiadneho
                            z našich členov a ani na žiadnej prevádzke.
                          </span>
                        )}

                        <br />
                        <br />
                        <div className="top">
                          <div className="price-wrap">
                            <Price
                              priceData={product?.finalPriceData}
                              options={{
                                hideVatPrice: true,
                                mainPriceClass: "price",
                              }}
                            />
                            {isPackage ? (
                              product?.standardPriceData?.price >
                              product?.finalPriceData?.price ? (
                                <span className="old-price">
                                  <Price
                                    priceData={product?.standardPriceData}
                                    options={{ hideVatPrice: true }}
                                  />
                                </span>
                              ) : (
                                ""
                              )
                            ) : product?.priceData?.price !==
                              product?.finalPriceData?.price ? (
                              <span className="old-price">
                                <Price
                                  priceData={product?.priceData}
                                  options={{ hideVatPrice: true }}
                                />
                              </span>
                            ) : (
                              ""
                            )}
                          </div>
                          <AddToCartButton
                            stores={get(product, "store", [])}
                            product={product}
                            options={{
                              eecProduct: {
                                name: get(product, "name"),
                                id: get(product, "id"),
                                finalPriceData: get(product, "finalPriceData"),
                                brand: get(product, "brand"),
                                categories: get(product, "categories"),
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="actions">
                  <div className="energy-label">
                    {!isPackage ? (
                      <EnergyLabel
                        labels={get(product, "labels")}
                        parameters={get(product, "detailedParameters")}
                      />
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="buttons">
                    <WishlistButton productId={get(product, "id")} />
                    <CompareButton productId={get(product, "id")} />
                    {/* <a href="#" className="compare"><i className="fas fa-balance-scale" aria-hidden="true"></i>Porovnať</a> */}
                    <div
                      className="fb-like"
                      data-href="https://developers.facebook.com/docs/plugins/"
                      data-width=""
                      data-layout="button"
                      data-action="like"
                      data-size="small"
                      data-share="true"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isPackage ? (
            <PackageProducts
              packageProducts={get(product, "productPackageProducts", [])}
            />
          ) : (
            <div className="tabs-section">
              <div className="heading text-center hidden-sm-down">
                <span
                  className={`tab ${activeTab === 1 ? `active` : ""}`}
                  onClick={(e) => setActiveTab(1)}
                >
                  Popis
                </span>
                {get(product, "detailedParameters") ? (
                  <span
                    className={`tab ${activeTab === 2 ? `active` : ""}`}
                    onClick={(e) => setActiveTab(2)}
                  >
                    Parametre
                  </span>
                ) : (
                  ""
                )}

                {/* <a href="#recenzie" className="tab">
                      Recenzie
                      <span>(13)</span>
                  </a> */}
              </div>
              <div className="content" id="longDesc">
                <>
                  <div
                    className="tab-heading hidden-sm-up"
                    onClick={(e) => setActiveTab(activeTab === 1 ? 2 : 1)}
                  >
                    Popis<i className="fas fa-caret-down"></i>
                  </div>
                  <div
                    className={`tab ${activeTab === 1 ? `show` : ""}`}
                    id="popis"
                  >
                    <div id="flix-inpage"></div>
                    {!isEmpty(get(product, "productDescription.description")) ? (
                      <div className="text mt-3">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: get(
                                product,
                                "productDescription.description"
                            ),
                          }}
                        />
                      </div>
                    ) : ""}
                  </div>
                </>

                {get(product, "detailedParameters") ? (
                  <>
                    <div
                      className="tab-heading hidden-sm-up"
                      onClick={(e) => setActiveTab(activeTab === 2 ? 1 : 2)}
                    >
                      Parametre<i className="fas fa-caret-down"></i>
                    </div>
                    <div
                      className={`tab ${activeTab === 2 ? `show` : ""}`}
                      id="parametre"
                    >
                      <Parameters product={product} />
                    </div>
                  </>
                ) : (
                  ""
                )}

                {/* <div className="tab-heading hidden-sm-up">
                  Recenzie<i className="fas fa-caret-down"></i>
                </div>
                <div className="tab" id="recenzie">
                    <div className="reviews">
                        <div className="review">
                            <div className="top-section">
                                <div className="number">4.00</div>
                                <div className="stars">
                                    <span className="star"><i className="fas fa-star"></i></span>
                                    <span className="star"><i className="fas fa-star"></i></span>
                                    <span className="star"><i className="fas fa-star"></i></span>
                                    <span className="star"><i className="fas fa-star"></i></span>
                                    <span className="star"><i className="far fa-star"></i></span>
                                </div>
                                <p>Hodnotilo 20 zákazníkov</p>
                            </div>
                            <div className="lines">
                                <table>
                                    <tr>
                                        <td>5 <i className="fas fa-star"></i></td>
                                        <td>
                                            <div className="line-wrap">
                                                <div className="line" style="width: 90%;"></div>
                                            </div>
                                        </td>
                                        <td>135 x</td>
                                    </tr>
                                    <tr>
                                        <td>4 <i className="fas fa-star"></i></td>
                                        <td>
                                            <div className="line-wrap">
                                                <div className="line" style="width: 50%;"></div>
                                            </div>
                                        </td>
                                        <td>64 x</td>
                                    </tr>
                                    <tr>
                                        <td>3 <i className="fas fa-star"></i></td>
                                        <td>
                                            <div className="line-wrap">
                                                <div className="line" style="width: 30%;"></div>
                                            </div>
                                        </td>
                                        <td>16 x</td>
                                    </tr>
                                    <tr>
                                        <td>2 <i className="fas fa-star"></i></td>
                                        <td>
                                            <div className="line-wrap">
                                                <div className="line" style="width: 20%;"></div>
                                            </div>
                                        </td>
                                        <td>0 x</td>
                                    </tr>
                                    <tr>
                                        <td>1 <i className="fas fa-star"></i></td>
                                        <td>
                                            <div className="line-wrap">
                                                <div className="line" style="width: 10%;"></div>
                                            </div>
                                        </td>
                                        <td>0 x</td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                        <div className="text-review">
                            <h2>Recenzie (32)</h2>
                            <div className="text-review__item">
                                <div className="text-review__item-heading">
                                    <div className="left">
                                        <div className="stars">
                                            <span className="star"><i className="fas fa-star"></i></span>
                                            <span className="star"><i className="fas fa-star"></i></span>
                                            <span className="star"><i className="fas fa-star"></i></span>
                                            <span className="star"><i className="fas fa-star"></i></span>
                                            <span className="star"><i className="far fa-star"></i></span>
                                        </div>
                                        <span className="name">Pavol</span>
                                    </div>
                                    <span className="date">24. 10. 2020</span>
                                </div>
                                <p>Maximálna spokojnosť. Vie si sám zhodnotiť intenzitu svetla v miestnosti, podľa toho nastavuje aj obraz, jas a tiene. Dobrý prístup a rozsah v rámci internetových služieb.</p>
                                <div className="pros-cons">
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <ul className="pros">
                                                <li><i className="fas fa-plus-square"></i>Špičkový dynamický obsah</li>
                                                <li><i className="fas fa-plus-square"></i>Množstvo vstupov</li>
                                            </ul>
                                        </div>
                                        <div className="col-lg-6">
                                            <ul className="cons">
                                                <li><i className="fas fa-minus-square"></i>Špičkový dynamický obsah</li>
                                                <li><i className="fas fa-minus-square"></i>Množstvo vstupov</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> */}
              </div>
            </div>
          )}
        </div>
        <script
          type="text/javascript"
          src="//media.flixfacts.com/js/loader.js"
          data-flix-distributor="11721"
          data-flix-language="sk"
          data-flix-brand={product?.brand?.name}
          data-flix-mpn=""
          data-flix-ean={product?.ean}
          data-flix-sku=""
          data-flix-button="flix-minisite"
          data-flix-inpage="flix-inpage"
          data-flix-button-image=""
          data-flix-price=""
          data-flix-fallback-language=""
          async
        />
        <script
          type="text/javascript"
          src="https://tracking.channelsight.com/api/tracking/v2/Init"
        />
      </section>
    </>
  );
};

export async function getServerSideProps(context) {
  return productPage.serverSideProps(context);
}

export default Product;
